import { create } from 'zustand';
import { AccommodationTable, Room, Bed } from '../types/accommodation';
import { collection, doc, getDoc, getDocs, deleteDoc, onSnapshot, updateDoc, query, where, setDoc, writeBatch } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { normalizePassportNumber } from '../utils/passportUtils';
import { calculateAge } from '../utils/dateUtils';
import * as XLSX from 'xlsx';
import { useBansStore } from './bansStore';
import { useSuspensionStore } from './suspensionStore';

type AccommodationState = {
  tables: Record<string, AccommodationTable>;
  loading: boolean;
  error: string | null;
  fetchTables: () => Promise<void>;
  createTable: (hotelId: string, hotelName: string, tableName: string, file: File) => Promise<void>;
  deleteTable: (tableId: string) => Promise<void>;
  getRoomDetails: (tableId: string, roomNumber: number) => Promise<RoomDetails>;
  assignPilgrim: (
    tableId: string, 
    roomNumber: number, 
    bedIndex: number, 
    passportNumber: string,
    dateRange: {
      checkIn: string;
      checkOut: string;
    },
    translations?: any
  ) => Promise<any>;
  removePilgrim: (
    tableId: string, 
    roomNumber: number, 
    bedIndex: number,
    pilgrimId: string,
    translations?: any
  ) => Promise<void>;
  updateRoomNumber: (tableId: string, oldRoomNumber: number, newRoomNumber: number) => Promise<void>;
  updateRooms: (tableId: string, newRooms: Room[]) => Promise<void>;
  removeAllAssignments: (tableId: string, excludedOrganizerIds?: string[], excludedCountries?: string[], excelFile?: File) => Promise<void>;
};

const parseExcelFile = async (file: File): Promise<Room[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error("Failed to read file data"));
          return;
        }
        
        const workbook = XLSX.read(data, { type: 'binary' });
        
        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          reject(new Error("Excel file does not contain any sheets"));
          return;
        }
        
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (!jsonData || jsonData.length === 0) {
          reject(new Error("No data found in Excel file"));
          return;
        }

        // Convert Excel data to Room objects
        const rooms: Room[] = [];
        
        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i] as any;
          const rowIndex = i + 2; // +2 because Excel is 1-based and we have a header row
          
          const roomType = row['room type']?.toLowerCase();
          // Store room number as string to allow alphanumeric values
          const roomNumber = String(row['room number']);

          // Validate room number (just check if it exists)
          if (!roomNumber) {
            reject(new Error(`Missing room number at row ${rowIndex}`));
            return;
          }

          // Validate room type
          if (!roomType || !['double', 'triple', 'quadruple'].includes(roomType)) {
            reject(new Error(`Invalid room type at row ${rowIndex}: ${roomType || 'empty'}`));
            return;
          }

          // Map room type to short form
          const typeMap = {
            'double': 'd',
            'triple': 't',
            'quadruple': 'q'
          };

          // Calculate capacity based on room type
          const capacityMap = {
            'd': 2,
            't': 3,
            'q': 4
          };

          const type = typeMap[roomType as keyof typeof typeMap];
          const capacity = capacityMap[type];

          rooms.push({
            n: roomNumber,
            t: type,
            c: capacity,
            o: 0,
            b: Array(capacity).fill(null).map(() => ({ a: [] }))
          });
        }

        // Check for duplicate room numbers
        const roomNumbers = new Set();
        for (const room of rooms) {
          if (roomNumbers.has(room.n)) {
            reject(new Error(`Duplicate room number: ${room.n}`));
            return;
          }
          roomNumbers.add(room.n);
        }

        resolve(rooms);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        reject(error || new Error('Failed to parse Excel file'));
      }
    };

    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      reject(new Error('Failed to read the file'));
    };

    try {
      reader.readAsBinaryString(file);
    } catch (error) {
      console.error('Error reading file:', error);
      reject(new Error('Failed to read the file: ' + (error as Error).message));
    }
  });
};

export const useAccommodationStore = create<AccommodationState>((set, get) => {
  let unsubscribers: { [key: string]: () => void } = {};

  return {
    tables: {},
    loading: false,
    error: null,

    fetchTables: async () => {
      set({ loading: true, error: null });
      try {
        const querySnapshot = await getDocs(collection(firestore, 'AccommodationTables'));
        const tables = {};

        querySnapshot.forEach((doc) => {
          tables[doc.id] = {
            ...doc.data(),
            i: doc.id
          };

          if (!unsubscribers[doc.id]) {
            unsubscribers[doc.id] = onSnapshot(
              doc.ref,
              (snapshot) => {
                if (snapshot.exists()) {
                  set((state) => ({
                    tables: {
                      ...state.tables,
                      [doc.id]: {
                        ...snapshot.data(),
                        i: doc.id
                      }
                    }
                  }));
                }
              },
              (error) => {
                console.error('Error in real-time listener:', error);
              }
            );
          }
        });

        set({ tables, loading: false });
      } catch (error: any) {
        console.error('Error fetching tables:', error);
        set({ error: error.message, loading: false });
      }
    },

    createTable: async (hotelId, hotelName, tableName, file) => {
      set({ loading: true, error: null });
      try {
        // Get current user role
        const userRole = localStorage.getItem('userRole');
        
        // Check for service suspension only for non-admin users
        if (userRole !== 'admin') {
          const isSuspended = await useSuspensionStore
            .getState()
            .checkServiceSuspension('all_accommodation');

          if (isSuspended) {
            throw new Error('Accommodation service is currently suspended');
          }

          const isHotelSuspended = await useSuspensionStore
            .getState()
            .checkServiceSuspension('specific_hotel', hotelId);

          if (isHotelSuspended) {
            throw new Error('This hotel is currently suspended');
          }
        }

        const userId = localStorage.getItem('userId');
        if (!userId) throw new Error('User not authenticated');

        // Parse the Excel file to get the rooms
        const rooms = await parseExcelFile(file);

        // Create a new table reference
        const tableRef = doc(collection(firestore, 'AccommodationTables'));
        
        // Prepare the table data
        const table: AccommodationTable = {
          i: tableRef.id,
          hi: hotelId,
          hn: hotelName,
          tn: tableName,
          r: rooms,
          ca: Date.now(),
          ua: Date.now(),
          cb: userId
        };

        // Save the table to Firestore
        await setDoc(tableRef, table);

        // Set up real-time listener for this table
        if (!unsubscribers[table.i]) {
          unsubscribers[table.i] = onSnapshot(
            doc(firestore, 'AccommodationTables', table.i),
            (snapshot) => {
              if (snapshot.exists()) {
                set((state) => ({
                  tables: {
                    ...state.tables,
                    [table.i]: {
                      ...snapshot.data(),
                      i: table.i
                    }
                  }
                }));
              }
            }
          );
        }

        // Update local state
        set(state => ({
          tables: {
            ...state.tables,
            [table.i]: table
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error creating table:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    deleteTable: async (tableId) => {
      console.log('Starting table deletion process...', { tableId });
      set({ loading: true, error: null });
      
      try {
        // Get table data first
        console.log('Fetching table data...');
        const tableRef = doc(firestore, 'AccommodationTables', tableId);
        const tableSnap = await getDoc(tableRef);
        
        if (!tableSnap.exists()) {
          throw new Error('Table not found');
        }

        const tableData = tableSnap.data();
        console.log('Table data fetched:', { 
          hotelName: tableData.hn,
          roomCount: tableData.r?.length 
        });

        // Get all pilgrim IDs from the table
        const pilgrimIds = new Set<string>();
        let assignmentCount = 0;
        
        tableData.r.forEach((room: Room) => {
          room.b.forEach((bed) => {
            if (bed.a) {
              assignmentCount += bed.a.length;
              bed.a.forEach((assignment) => {
                pilgrimIds.add(assignment.pid);
              });
            }
          });
        });

        console.log('Found assignments:', {
          uniquePilgrims: pilgrimIds.size,
          totalAssignments: assignmentCount
        });

        // Process pilgrim IDs in batches of 30 (Firestore limit)
        const BATCH_SIZE = 30;
        const batches = [];
        const pilgrimIdsArray = Array.from(pilgrimIds);
        
        for (let i = 0; i < pilgrimIdsArray.length; i += BATCH_SIZE) {
          batches.push(pilgrimIdsArray.slice(i, i + BATCH_SIZE));
        }

        console.log('Split into batches:', {
          totalBatches: batches.length,
          batchSize: BATCH_SIZE
        });

        // Process each batch
        let processedPilgrims = 0;
        let updatedSelectors = 0;

        for (const batch of batches) {
          const selectorRef = collection(firestore, 'PilgrimSelector');
          const selectorQuery = query(selectorRef, where('pilgrimId', 'in', batch));
          const selectorSnapshot = await getDocs(selectorQuery);

          const batchWrite = writeBatch(firestore);

          selectorSnapshot.forEach(doc => {
            const selectorData = doc.data();
            const updatedAccommodation = { ...selectorData.isAccommodated };
            updatedAccommodation[tableData.hn] = false;

            batchWrite.update(doc.ref, {
              isAccommodated: updatedAccommodation,
              updatedAt: Date.now()
            });
            updatedSelectors++;
          });

          await batchWrite.commit();
          processedPilgrims += batch.length;
          
          console.log('Batch processed:', {
            processedInBatch: batch.length,
            totalProcessed: processedPilgrims,
            updatedSelectors
          });
        }

        // Delete the table
        console.log('Deleting table document...');
        await deleteDoc(tableRef);
        
        // Clean up listener
        if (unsubscribers[tableId]) {
          unsubscribers[tableId]();
          delete unsubscribers[tableId];
        }

        // Update local state
        set(state => {
          const newTables = { ...state.tables };
          delete newTables[tableId];
          return { tables: newTables, loading: false };
        });

        console.log('Table deletion completed successfully:', {
          processedPilgrims,
          updatedSelectors
        });
      } catch (error: any) {
        console.error('Error deleting table:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    getRoomDetails: async (tableId, roomNumber) => {
      console.log('Loading room details:', {tableId, roomNumber});
      try {
        const tableRef = doc(firestore, 'AccommodationTables', tableId);
        const tableSnap = await getDoc(tableRef);

        if (!tableSnap.exists()) {
          throw new Error('Accommodation table not found');
        }

        const table = tableSnap.data();
        // ALWAYS convert roomNumber to string for comparison
        const roomNumberStr = String(roomNumber);
        console.log('Searching for room with number:', roomNumberStr);
        
        const room = table.r.find((r: Room) => r.n === roomNumberStr);

        if (!room) {
          console.error(`Room ${roomNumberStr} not found in table. Available rooms:`, 
            table.r.map((r: Room) => r.n));
          throw new Error('Room not found');
        }

        console.log(`Found room ${roomNumberStr} with type ${room.t} and ${room.b.length} beds`);
        return {
          n: room.n,
          t: room.t,
          b: room.b
        };
      } catch (error: any) {
        console.error('Error getting room details:', error);
        throw error;
      }
    },

    assignPilgrim: async (tableId, roomNumber, bedIndex, passportNumber, dateRange, translations) => {
      set({ loading: true, error: null });
      try {
        console.log('Starting pilgrim assignment with data:', {
          tableId,
          roomNumber,
          bedIndex,
          passportNumber,
          dateRange
        });

        // Get current user role
        const userRole = localStorage.getItem('userRole');
        const currentUserId = localStorage.getItem('userId');

        // Check for service suspension only for non-admin users
        if (userRole !== 'admin') {
          // Check for service suspension
          const isSuspended = await useSuspensionStore
            .getState()
            .checkServiceSuspension('all_accommodation');

          if (isSuspended) {
            throw new Error('Accommodation service is currently suspended');
          }

          if (!currentUserId) {
            throw new Error('You are not authorized to perform this action');
          }

          // Check for user ban
          const isUserBanned = await useBansStore
            .getState()
            .checkUserBans(currentUserId, 'accommodation');

          if (isUserBanned) {
            throw new Error('You are banned from using the accommodation service');
          }
        }

        // Get table data
        const tableRef = doc(firestore, 'AccommodationTables', tableId);
        const tableSnap = await getDoc(tableRef);

        if (!tableSnap.exists()) {
          throw new Error('Accommodation table not found');
        }

        const table = tableSnap.data();
        console.log('Found table:', {
          id: tableId,
          hotelName: table.hn,
          roomCount: table.r?.length
        });

        // Check hotel suspension for non-admin users
        if (userRole !== 'admin') {
          const isHotelSuspended = await useSuspensionStore
            .getState()
            .checkServiceSuspension('specific_hotel', table.hi);

          if (isHotelSuspended) {
            throw new Error('This hotel is currently suspended');
          }
        }

        // Find pilgrim in PilgrimSelector
        const normalizedPassport = normalizePassportNumber(passportNumber).toUpperCase();
        console.log('Searching for pilgrim with passport:', normalizedPassport);

        const selectorRef = collection(firestore, 'PilgrimSelector');
        const selectorQuery = query(
          selectorRef,
          where('pilgrimData.passportNumber', '==', normalizedPassport),
          where('status', '==', 'active')
        );

        const selectorSnapshot = await getDocs(selectorQuery);
        if (selectorSnapshot.empty) {
          throw new Error('No active pilgrim found with this passport number');
        }

        const selectorDoc = selectorSnapshot.docs[0];
        const selectorData = selectorDoc.data();

        console.log('Found pilgrim selector data:', selectorData);

        // Check permissions
        if (userRole === 'guide' && selectorData.guideId !== currentUserId) {
          throw new Error('You do not have permission to assign this pilgrim');
        }

        if (userRole === 'organizer' && selectorData.organizerId !== currentUserId) {
          throw new Error('You do not have permission to assign this pilgrim');
        }

        // Find room
        // Convert room number to string for comparison
        const roomNumberStr = String(roomNumber);
        const roomIndex = table.r.findIndex((r: Room) => r.n === roomNumberStr);
        if (roomIndex === -1) {
          throw new Error('Room not found');
        }

        const room = table.r[roomIndex];
        console.log('Found room:', {
          number: room.n,
          type: room.t,
          capacity: room.c,
          occupied: room.o
        });

        // Create assignment with all required fields
        const newAssignment = {
          pid: selectorDoc.id,
          pn: selectorData.pilgrimData.name,
          ci: dateRange.checkIn, // Use string date format
          co: dateRange.checkOut, // Use string date format
          g: selectorData.pilgrimData.gender === 'male' ? 'M' : 'F',
          gid: selectorData.guideId,
          gn: selectorData.guideData.name,
          oid: selectorData.organizerId,
          on: selectorData.organizerData.name,
          age: calculateAge(selectorData.pilgrimData.dateOfBirth)
        };

        console.log('Created assignment:', newAssignment);

        // Update room data
        const updatedRooms = [...table.r];
        const updatedBed = { ...room.b[bedIndex] };
        
        if (!updatedBed.a) {
          updatedBed.a = [];
        }

        updatedBed.a.push(newAssignment);
        updatedRooms[roomIndex].b[bedIndex] = updatedBed;
        updatedRooms[roomIndex].o++;

        console.log('Updating table with new assignment:', {
          roomIndex,
          bedIndex,
          newOccupancy: updatedRooms[roomIndex].o
        });

        // Start batch operation
        const batch = writeBatch(firestore);

        // Update table in Firestore
        batch.update(tableRef, {
          r: updatedRooms,
          ua: Date.now()
        });

        // Update accommodation status in PilgrimSelector
        const updatedAccommodation = { ...selectorData.isAccommodated } || {};
        updatedAccommodation[table.hn] = true;

        batch.update(selectorDoc.ref, {
          isAccommodated: updatedAccommodation,
          updatedAt: Date.now()
        });

        // Commit all changes
        await batch.commit();

        // Update local state
        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: {
              ...state.tables[tableId],
              r: updatedRooms,
              ua: Date.now()
            }
          },
          loading: false
        }));

        console.log('Assignment completed successfully');
        return selectorData.pilgrimData;
      } catch (error: any) {
        console.error('Error assigning pilgrim:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    removePilgrim: async (tableId, roomNumber, bedIndex, pilgrimId, translations) => {
      set({ loading: true, error: null });
      try {
        // Get current user role
        const userRole = localStorage.getItem('userRole');
        const currentUserId = localStorage.getItem('userId');

        // Check for service suspension only for non-admin users
        if (userRole !== 'admin') {
          const isSuspended = await useSuspensionStore
            .getState()
            .checkServiceSuspension('all_accommodation');

          if (isSuspended) {
            throw new Error('Accommodation service is currently suspended');
          }

          if (!currentUserId) {
            throw new Error('You are not authorized to perform this action');
          }

          const isUserBanned = await useBansStore
            .getState()
            .checkUserBans(currentUserId, 'accommodation');

          if (isUserBanned) {
            throw new Error('You are banned from using the accommodation service');
          }
        }

        // Start batch operation
        const batch = writeBatch(firestore);

        // Get table data
        const tableRef = doc(firestore, 'AccommodationTables', tableId);
        const tableSnap = await getDoc(tableRef);

        if (!tableSnap.exists()) {
          throw new Error('Accommodation table not found');
        }

        const tableData = tableSnap.data();
        
        // Check hotel suspension for non-admin users
        if (userRole !== 'admin') {
          const isHotelSuspended = await useSuspensionStore
            .getState()
            .checkServiceSuspension('specific_hotel', tableData.hi);

          if (isHotelSuspended) {
            throw new Error('This hotel is currently suspended');
          }
        }

        // Convert roomNumber to string for comparison
        const roomNumberStr = String(roomNumber);
        const roomIndex = tableData.r.findIndex((r: Room) => r.n === roomNumberStr);
        if (roomIndex === -1) {
          throw new Error('Room not found');
        }

        const room = tableData.r[roomIndex];
        const bed = room.b[bedIndex];

        const assignmentIndex = bed.a?.findIndex(a => a.pid === pilgrimId);
        if (assignmentIndex === undefined || assignmentIndex === -1) {
          throw new Error('Assignment not found');
        }

        // Check permissions
        const assignment = bed.a[assignmentIndex];
        if (userRole === 'guide' && assignment.gid !== currentUserId) {
          throw new Error('You do not have permission to remove this pilgrim');
        }
        if (userRole === 'organizer' && assignment.oid !== currentUserId) {
          throw new Error('You do not have permission to remove this pilgrim');
        }

        // Update room data
        const updatedRooms = [...tableData.r];
        const updatedBed = { ...room.b[bedIndex] };
        updatedBed.a = updatedBed.a?.filter((_, index) => index !== assignmentIndex);
        updatedRooms[roomIndex].b[bedIndex] = updatedBed;
        updatedRooms[roomIndex].o = Math.max(0, room.o - 1);

        // Update table
        batch.update(tableRef, {
          r: updatedRooms,
          ua: Date.now()
        });

        // Get the PilgrimSelector document
        const selectorRef = collection(firestore, 'PilgrimSelector');
        const selectorQuery = query(selectorRef, where('pilgrimId', '==', pilgrimId));
        const selectorSnapshot = await getDocs(selectorQuery);

        if (!selectorSnapshot.empty) {
          const selectorDoc = selectorSnapshot.docs[0];
          const selectorData = selectorDoc.data();

          // Update the isAccommodated status for this hotel
          const updatedAccommodation = { ...selectorData.isAccommodated } || {};
          updatedAccommodation[tableData.hn] = false;

          // Update the selector document
          batch.update(selectorDoc.ref, {
            isAccommodated: updatedAccommodation,
            updatedAt: Date.now()
          });
        }

        // Commit all changes
        await batch.commit();

        // Update local state
        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: {
              ...state.tables[tableId],
              r: updatedRooms,
              ua: Date.now()
            }
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error removing pilgrim:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    updateRoomNumber: async (tableId, oldRoomNumber, newRoomNumber) => {
      set({ loading: true, error: null });
      try {
        const tableRef = doc(firestore, 'AccommodationTables', tableId);
        const tableSnap = await getDoc(tableRef);

        if (!tableSnap.exists()) {
          throw new Error('Accommodation table not found');
        }

        const table = tableSnap.data();
        const roomExists = table.r.some((r: Room) => r.n === newRoomNumber.toString());
        
        if (roomExists) {
          throw new Error('Room number already exists');
        }

        // Convert to string for comparison
        const oldRoomNumberStr = String(oldRoomNumber);
        const roomIndex = table.r.findIndex((r: Room) => r.n === oldRoomNumberStr);
        if (roomIndex === -1) {
          throw new Error('Room not found');
        }

        const updatedRooms = [...table.r];
        updatedRooms[roomIndex] = {
          ...updatedRooms[roomIndex],
          n: newRoomNumber.toString()
        };

        await updateDoc(tableRef, {
          r: updatedRooms,
          ua: Date.now()
        });

        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: {
              ...state.tables[tableId],
              r: updatedRooms,
              ua: Date.now()
            }
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error updating room number:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    updateRooms: async (tableId: string, newRooms: Room[]) => {
      set({ loading: true, error: null });
      try {
        const tableRef = doc(firestore, 'AccommodationTables', tableId);
        await updateDoc(tableRef, {
          r: newRooms,
          ua: Date.now()
        });

        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: {
              ...state.tables[tableId],
              r: newRooms,
              ua: Date.now()
            }
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error updating rooms:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    removeAllAssignments: async (tableId: string, excludedOrganizerIds: string[] = [], excludedCountries: string[] = [], excelFile?: File) => {
      set({ loading: true, error: null });
      try {
        // Method 3: Remove specific pilgrims based on excel file
        if (excelFile) {
          console.log('Using Excel file to remove specific pilgrims');
          
          // Read the Excel file
          const reader = new FileReader();
          const fileData = await new Promise<string>((resolve, reject) => {
            reader.onload = (e) => resolve(e.target?.result as string);
            reader.onerror = (e) => reject(e);
            reader.readAsBinaryString(excelFile);
          });
          
          const workbook = XLSX.read(fileData, { type: 'binary' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];
          
          // Extract passport numbers
          const passportNumbers = jsonData
            .map(row => {
              // Check different possible column names
              const passport = row.passport || row.passport_number || row.passportNumber || row.Passport || row['Passport Number'];
              return passport ? normalizePassportNumber(passport.toString()) : null;
            })
            .filter(p => p) as string[];
          
          if (passportNumbers.length === 0) {
            throw new Error('No valid passport numbers found in the Excel file');
          }
          
          console.log(`Found ${passportNumbers.length} passport numbers in Excel file`);

          // Get table data
          const tableRef = doc(firestore, 'AccommodationTables', tableId);
          const tableSnap = await getDoc(tableRef);

          if (!tableSnap.exists()) {
            throw new Error('Accommodation table not found');
          }

          const tableData = tableSnap.data();
          console.log('Table data fetched:', { hotelName: tableData.hn });
          
          // First, map passport numbers to pilgrim IDs by querying Firestore
          const pilgrimIds = new Set<string>();
          const pilgrimMap = new Map<string, string>(); // Map passport to pilgrim ID
          
          // Process in smaller batches due to Firestore limitations
          const PASSPORT_BATCH_SIZE = 10;
          for (let i = 0; i < passportNumbers.length; i += PASSPORT_BATCH_SIZE) {
            const batch = passportNumbers.slice(i, i + PASSPORT_BATCH_SIZE);
            
            // Find pilgrims by these passports
            const pilgrimsRef = collection(firestore, 'Pilgrims');
            const pilgrimQueries = batch.map(passportNumber => {
              return getDocs(query(pilgrimsRef, where('Passport_Number', '==', passportNumber)));
            });
            
            const results = await Promise.all(pilgrimQueries);
            
            // Process results
            results.forEach((querySnapshot, index) => {
              if (!querySnapshot.empty) {
                const pilgrimId = querySnapshot.docs[0].id;
                const passportNumber = batch[index];
                pilgrimIds.add(pilgrimId);
                pilgrimMap.set(passportNumber, pilgrimId);
              }
            });
          }
          
          console.log(`Mapped ${pilgrimIds.size} pilgrim IDs from passports`);
          
          if (pilgrimIds.size === 0) {
            throw new Error('No matching pilgrims found for the provided passport numbers');
          }
          
          // Now remove pilgrim assignments from the accommodation table
          const removedAssignments: string[] = [];
          const notFoundPassports: string[] = [];
          const updatedRooms = tableData.r.map((room: any) => {
            let roomOccupancy = 0;
            
            const updatedBeds = room.b.map((bed: any) => {
              if (!bed.a || bed.a.length === 0) return bed;
              
              // Keep assignments for pilgrims not in the Excel file
              const filteredAssignments = bed.a.filter((assignment: any) => {
                const shouldRemove = pilgrimIds.has(assignment.pid);
                if (shouldRemove) {
                  removedAssignments.push(assignment.pid);
                }
                return !shouldRemove;
              });
              
              // Update bed
              roomOccupancy += filteredAssignments.length;
              return { ...bed, a: filteredAssignments };
            });
            
            // Return updated room
            return { 
              ...room, 
              b: updatedBeds,
              o: roomOccupancy
            };
          });
          
          // Find passports that weren't found in the accommodation
          passportNumbers.forEach(passport => {
            const pilgrimId = pilgrimMap.get(passport);
            if (pilgrimId && !removedAssignments.includes(pilgrimId)) {
              notFoundPassports.push(passport);
            }
          });
          
          console.log('Assignment removal summary:', {
            removedAssignments: removedAssignments.length,
            notFound: notFoundPassports.length
          });
          
          // Update PilgrimSelector documents for affected pilgrims
          const BATCH_SIZE = 450; // Firestore batch limit
          
          // Process pilgrim IDs that were actually found and removed
          const pilgrimIdsToUpdate = removedAssignments;
          let currentBatch = writeBatch(firestore);
          let operationCount = 0;
          
          for (const pilgrimId of pilgrimIdsToUpdate) {
            // Update PilgrimSelector document
            const selectorRef = collection(firestore, 'PilgrimSelector');
            const selectorQuery = query(selectorRef, where('pilgrimId', '==', pilgrimId));
            const selectorSnapshot = await getDocs(selectorQuery);
            
            if (!selectorSnapshot.empty) {
              const selectorDoc = selectorSnapshot.docs[0];
              const selectorData = selectorDoc.data();
              
              // Update accommodation status
              const updatedAccommodation = { ...selectorData.isAccommodated } || {};
              updatedAccommodation[tableData.hn] = false;
              
              currentBatch.update(selectorDoc.ref, {
                isAccommodated: updatedAccommodation,
                updatedAt: Date.now()
              });
              
              operationCount++;
              
              // Commit batch if needed
              if (operationCount >= BATCH_SIZE) {
                await currentBatch.commit();
                currentBatch = writeBatch(firestore);
                operationCount = 0;
              }
            }
          }
          
          // Commit any remaining operations
          if (operationCount > 0) {
            await currentBatch.commit();
          }
          
          // Update table with new assignments
          await updateDoc(tableRef, {
            r: updatedRooms,
            ua: Date.now()
          });
          
          // Update local state
          set((state) => ({
            tables: {
              ...state.tables,
              [tableId]: {
                ...state.tables[tableId],
                r: updatedRooms,
                ua: Date.now()
              }
            },
            loading: false
          }));
          
          console.log(`Excel-based removal complete: removed ${removedAssignments.length} pilgrims`);
          return;
        }
        
        console.log('Starting to remove all assignments for table:', tableId);
        console.log('Excluded organizer IDs:', excludedOrganizerIds);
        console.log('Excluded countries:', excludedCountries);

        // Get table data
        const tableRef = doc(firestore, 'AccommodationTables', tableId);
        const tableSnap = await getDoc(tableRef);

        if (!tableSnap.exists()) {
          throw new Error('Accommodation table not found');
        }

        const tableData = tableSnap.data();
        
        // Collect all pilgrim IDs from assignments
        const pilgrimIds = new Set<string>();
        const excludedPilgrimIds = new Set<string>();
        let totalAssignments = 0;
        let excludedAssignments = 0;
        
        // If we have country exclusions, we need to fetch PilgrimSelector data to check countries
        let pilgrimCountryMap = new Map<string, string>();
        if (excludedCountries.length > 0) {
          console.log('Fetching pilgrim country information for country exclusions');
          const selectorRef = collection(firestore, 'PilgrimSelector');
          const selectorSnapshot = await getDocs(selectorRef);
          
          selectorSnapshot.forEach(doc => {
            const data = doc.data();
            if (data.pilgrimData?.residenceCountry) {
              pilgrimCountryMap.set(doc.id, data.pilgrimData.residenceCountry);
            }
          });
          
          console.log(`Loaded country information for ${pilgrimCountryMap.size} pilgrims`);
        }
        
        // Count assignments and gather unique pilgrim IDs
        tableData.r.forEach((room: Room) => {
          room.b.forEach((bed: Bed) => {
            if (bed.a && bed.a.length > 0) {
              bed.a.forEach(assignment => {
                totalAssignments++;
                
                // Check if we should exclude this assignment based on the organizer
                const isOrganizerExcluded = excludedOrganizerIds.includes(assignment.oid);

                // Check if we should exclude this assignment based on country
                const pilgrimCountry = pilgrimCountryMap.get(assignment.pid);
                const isCountryExcluded = excludedCountries.length > 0 && pilgrimCountry && excludedCountries.includes(pilgrimCountry);
                
                if (isOrganizerExcluded || isCountryExcluded) {
                  excludedPilgrimIds.add(assignment.pid);
                  excludedAssignments++;
                } else {
                  pilgrimIds.add(assignment.pid);
                }
              });
            }
          });
        });

        console.log(`Found ${totalAssignments} assignments: ${pilgrimIds.size} to remove, ${excludedAssignments} to preserve`);

        if (pilgrimIds.size === 0 && excludedPilgrimIds.size === 0) {
          throw new Error('No assignments found to remove');
        }

        // Update all rooms to remove assignments that aren't excluded
        const updatedRooms = tableData.r.map((room: Room) => {
          // Reset occupancy count
          let roomOccupancy = 0;
          
          // Update beds to remove non-excluded assignments
          const updatedBeds = room.b.map((bed) => {
            if (!bed.a || bed.a.length === 0) {
              return bed;
            }
            
            // Keep only the assignments from excluded organizers/countries
            const keptAssignments = bed.a.filter(assignment => 
              excludedPilgrimIds.has(assignment.pid)
            );
            
            // Update room occupancy count
            roomOccupancy += keptAssignments.length;
            
            return {
              ...bed,
              a: keptAssignments
            };
          });
          
          return {
            ...room,
            b: updatedBeds,
            o: roomOccupancy  // Update occupancy
          };
        });

        // Process pilgrim IDs in batches of 30 (Firestore limit)
        const BATCH_SIZE = 30;
        const batches = [];
        const pilgrimIdsArray = Array.from(pilgrimIds);
        
        for (let i = 0; i < pilgrimIdsArray.length; i += BATCH_SIZE) {
          batches.push(pilgrimIdsArray.slice(i, i + BATCH_SIZE));
        }

        console.log('Split into batches:', {
          totalBatches: batches.length,
          batchSize: BATCH_SIZE
        });

        // Process each batch
        let processedPilgrims = 0;
        let updatedSelectors = 0;

        for (const batch of batches) {
          const selectorRef = collection(firestore, 'PilgrimSelector');
          const selectorQuery = query(selectorRef, where('pilgrimId', 'in', batch));
          const selectorSnapshot = await getDocs(selectorQuery);

          const batchWrite = writeBatch(firestore);

          selectorSnapshot.forEach(doc => {
            const selectorData = doc.data();
            const updatedAccommodation = { ...selectorData.isAccommodated };
            updatedAccommodation[tableData.hn] = false;

            batchWrite.update(doc.ref, {
              isAccommodated: updatedAccommodation,
              updatedAt: Date.now()
            });
            updatedSelectors++;
          });

          await batchWrite.commit();
          processedPilgrims += batch.length;
          
          console.log('Batch processed:', {
            processedInBatch: batch.length,
            totalProcessed: processedPilgrims,
            updatedSelectors
          });
        }

        // Update table with new assignments
        await updateDoc(tableRef, {
          r: updatedRooms,
          ua: Date.now()
        });
        
        // Update local state
        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: {
              ...state.tables[tableId],
              r: updatedRooms,
              ua: Date.now()
            }
          },
          loading: false
        }));

        console.log('All assignments removed successfully:', {
          roomsUpdated: updatedRooms.length,
          pilgrimsRemoved: pilgrimIds.size,
          pilgrimsExcluded: excludedPilgrimIds.size
        });
      } catch (error: any) {
        console.error('Error removing assignments:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    }
  };
});